import { blue, blueGrey, cyan, deepOrange, green, grey, indigo, lightBlue, lightGreen, orange, purple, red, teal } from "@mui/material/colors";
import { DeploymentHostEnvironment } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/deployment_host_environment_pb";
import { GrVmware } from "react-icons/gr";
import { HiOutlineServer } from "react-icons/hi";
import { MdOutlineCloudQueue } from "react-icons/md";
import {
    SiAlibabacloud,
    SiAmazonaws,
    SiApachecloudstack,
    SiDigitalocean,
    SiGooglecloud,
    SiHetzner,
    SiHuawei,
    SiIbm,
    SiKubernetes,
    SiLinux,
    SiMicrosoft,
    SiMicrosoftazure,
    SiNutanix,
    SiOpennebula,
    SiOpenstack,
    SiOracle,
    SiRedhat,
    SiUpcloud,
    SiVirtualbox,
    SiVultr,
} from "react-icons/si";

// Shared by host icons, dashboard distributions, and topology containers.
// Product names stay literal; callers localize the unknown/on-premises/DR role labels.
export const HOST_PLATFORMS = {
    aws: { name: "AWS", color: orange[400], Icon: SiAmazonaws },
    azure: { name: "Azure", color: lightBlue[500], Icon: SiMicrosoftazure },
    gcp: { name: "GCP", color: blue[400], Icon: SiGooglecloud },
    digitalocean: { name: "DigitalOcean", color: blue[500], Icon: SiDigitalocean },
    vmware: { name: "VMware", color: blue[600], Icon: GrVmware },
    hyperv: { name: "Hyper-V", color: cyan[500], Icon: SiMicrosoft },
    softlayer: { name: "SoftLayer", color: blueGrey[400], Icon: SiIbm },
    openstack: { name: "OpenStack", color: red[600], Icon: SiOpenstack },
    vultr: { name: "Vultr", color: lightBlue[400], Icon: SiVultr },
    nutanix: { name: "Nutanix", color: green[500], Icon: SiNutanix },
    kvm: { name: "KVM", color: deepOrange[400], Icon: SiLinux },
    ovirt: { name: "oVirt", color: lightGreen[700], Icon: SiRedhat },
    cloudstack: { name: "CloudStack", color: indigo[700], Icon: SiApachecloudstack },
    kubevirt: { name: "KubeVirt", color: blue[700], Icon: SiKubernetes },
    xen: { name: "Xen", color: indigo[300], Icon: HiOutlineServer },
    virtualbox: { name: "VirtualBox", color: blue[500], Icon: SiVirtualbox },
    opennebula: { name: "OpenNebula", color: orange[600], Icon: SiOpennebula },
    linode: { name: "Linode", color: lightGreen[600], Icon: MdOutlineCloudQueue },
    upcloud: { name: "UpCloud", color: purple[400], Icon: SiUpcloud },
    huawei: { name: "Huawei Cloud", color: red[500], Icon: SiHuawei },
    oci: { name: "Oracle Cloud", color: red[500], Icon: SiOracle },
    alibaba: { name: "Alibaba Cloud", color: orange[500], Icon: SiAlibabacloud },
    tencent: { name: "Tencent Cloud", color: blue[500], Icon: MdOutlineCloudQueue },
    hetzner: { name: "Hetzner", color: red[500], Icon: SiHetzner },
    // Recipe destinations can identify Zadara even though the host enum cannot.
    zadara: { name: "Zadara", color: teal[400], Icon: MdOutlineCloudQueue },
    generic: { name: null as string | null, color: grey[500], Icon: HiOutlineServer },
} as const;

export type HostPlatformId = keyof typeof HOST_PLATFORMS;

const HostEnv = DeploymentHostEnvironment.DeploymentHostEnvironment;
const environmentPlatforms = {
    [HostEnv.OTHER]: "generic",
    [HostEnv.AWS]: "aws",
    [HostEnv.AZURE]: "azure",
    [HostEnv.GCP]: "gcp",
    [HostEnv.DO]: "digitalocean",
    [HostEnv.VMWARE]: "vmware",
    [HostEnv.HYPERV]: "hyperv",
    [HostEnv.SOFTLAYER]: "softlayer",
    [HostEnv.OPENSTACK]: "openstack",
    [HostEnv.VULTR]: "vultr",
    [HostEnv.NUTANIX]: "nutanix",
    [HostEnv.KVM]: "kvm",
    [HostEnv.OVIRT]: "ovirt",
    [HostEnv.CLOUDSTACK]: "cloudstack",
    [HostEnv.KUBEVIRT]: "kubevirt",
    [HostEnv.XEN]: "xen",
    [HostEnv.VIRTUALBOX]: "virtualbox",
    [HostEnv.OPENNEBULA]: "opennebula",
    [HostEnv.LINODE]: "linode",
    [HostEnv.UPCLOUD]: "upcloud",
    [HostEnv.HUAWEI]: "huawei",
    [HostEnv.OCI]: "oci",
    [HostEnv.ALIBABA]: "alibaba",
    [HostEnv.TENCENT]: "tencent",
    [HostEnv.HETZNER]: "hetzner",
    [HostEnv.UNKNOWN]: "generic",
} as const satisfies Record<DeploymentHostEnvironment.DeploymentHostEnvironment, HostPlatformId>;

export const hostPlatformFromEnvironment = (environment?: DeploymentHostEnvironment.DeploymentHostEnvironment): HostPlatformId =>
    environmentPlatforms[environment] ?? "generic";
